import React from 'react';

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { SeriesPoint } from '../types';

type Props = {
  data: SeriesPoint[];
  selectedDay?: string | null;
  onDayClick?: (day: string) => void;
};

const VISITS_COLOR = '#F6A96B';
const SURVEYS_COLOR = '#E26D8E';
const CONVERSION_COLOR = '#4FA8DA';
const CLICKS_COLOR = '#9B8AFB';

const ConversionChart: React.FC<Props> = ({ data, selectedDay, onDayClick }) => {
  if (data.length === 0) {
    return (
      <p className="py-12 text-center text-gray-3.0">
        No visits or surveys found for the selected range.
      </p>
    );
  }

  const handleChartClick = (state: { activeLabel?: string }): void => {
    const label = state?.activeLabel;
    if (label && onDayClick) {
      onDayClick(label);
    }
  };

  const barOpacity = (day: string): number => (!selectedDay || day === selectedDay ? 1 : 0.25);

  return (
    <ResponsiveContainer width="100%" height={420}>
      <ComposedChart
        data={data}
        margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
        onClick={handleChartClick}
        style={{ cursor: onDayClick ? 'pointer' : 'default' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#E1E5EF" />
        <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
        <YAxis
          yAxisId="right"
          orientation="right"
          unit="%"
          domain={[0, 100]}
          tick={{ fontSize: 12 }}
        />
        <Tooltip />
        <Legend />
        {selectedDay && (
          <ReferenceLine
            yAxisId="left"
            x={selectedDay}
            stroke={CONVERSION_COLOR}
            strokeWidth={2}
            strokeDasharray="4 3"
            label={{ value: selectedDay, position: 'top', fontSize: 11, fill: CONVERSION_COLOR }}
          />
        )}
        <Bar yAxisId="left" dataKey="visitas" name="Visitas" fill={VISITS_COLOR}>
          {data.map((entry) => (
            <Cell key={`visitas-${entry.dia}`} fillOpacity={barOpacity(entry.dia)} />
          ))}
        </Bar>
        <Bar yAxisId="left" dataKey="encuestas" name="Encuestas respondidas" fill={SURVEYS_COLOR}>
          {data.map((entry) => (
            <Cell key={`encuestas-${entry.dia}`} fillOpacity={barOpacity(entry.dia)} />
          ))}
        </Bar>
        <Line
          yAxisId="right"
          type="linear"
          dataKey="click_conversion_rate"
          name="Conversion Rate Click"
          unit="%"
          stroke={CLICKS_COLOR}
          strokeWidth={2}
          dot={{ r: 4, fill: CLICKS_COLOR, strokeWidth: 0 }}
          activeDot={{ r: 6 }}
          connectNulls
        />
        <Line
          yAxisId="right"
          type="linear"
          dataKey="conversion_rate"
          name="Conversion rate de respuestas"
          unit="%"
          stroke={CONVERSION_COLOR}
          strokeWidth={2}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default ConversionChart;
